// src/components/BulkUpload/BulkUpload.tsx
import React, { useMemo, useState } from "react";
import api from "../../api";

type BulkUploadResult = {
  ok: boolean;
  mode: string;
  created: number;
  updated: number;
  skipped: number;
  errors: { row: number; error: string }[];
  issued_pins?: { org_code: string; student_id: string; pin: string }[];
  pins_truncated?: boolean;
};

function getAdminToken(): string {
  return (
    localStorage.getItem("admin_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    ""
  ).trim();
}

export default function BulkUpload() {
  const [mode, setMode] = useState<"combined" | "orgs" | "students">("combined");
  const [file, setFile] = useState<File | null>(null);

  const [returnPins, setReturnPins] = useState(true);
  const [rotatePins, setRotatePins] = useState(false);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState<BulkUploadResult | null>(null);

  const canSubmit = useMemo(() => !!file && !loading, [file, loading]);

  const downloadPinsCsv = () => {
    if (!result?.issued_pins?.length) return;

    const rows = [
      ["org_code", "student_id", "pin"],
      ...result.issued_pins.map((r) => [r.org_code, r.student_id, r.pin]),
    ];

    const csv = rows
      .map((r) =>
        r
          .map((v) => {
            const s = String(v ?? "");
            return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `student_pins_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr("");
    setResult(null);

    if (!file) {
      setErr("Please choose a CSV file.");
      return;
    }

    const token = getAdminToken();
    if (!token) {
      setErr("Admin token missing. Please log in as admin again.");
      return;
    }

    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("mode", mode);
      form.append("return_pins", returnPins ? "true" : "false");
      form.append("rotate_pins", rotatePins ? "true" : "false");

      const res = await api.post("/admin/bulk_upload", form, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setResult(res.data as BulkUploadResult);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || "Bulk upload failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "22px 16px", minHeight: "calc(100vh - 120px)" }}>
      <div style={{ maxWidth: 980, margin: "0 auto" }}>
        <div
          style={{
            background: "rgba(255,255,255,0.92)",
            borderRadius: 18,
            border: "1px solid rgba(0,0,0,0.08)",
            padding: 18,
            boxShadow: "0 14px 40px rgba(0,0,0,0.10)",
          }}
        >
          <h2 style={{ margin: "4px 0 10px", fontSize: 22, fontWeight: 950 }}>
            Bulk Upload (Orgs + Students)
          </h2>

          <div style={{ opacity: 0.85, marginBottom: 14, fontWeight: 600, lineHeight: 1.45 }}>
            Upload your CSV. If you enable <b>Return issued PINs</b>, you can export the PINs and students can log in successfully.
            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
              Tip: If you check <b>Rotate existing PINs</b>, old PINs stop working.
            </div>
          </div>

          {err ? (
            <div
              style={{
                background: "#ffecec",
                border: "1px solid #ffb3b3",
                color: "#7a0000",
                padding: 10,
                borderRadius: 12,
                marginBottom: 12,
                fontWeight: 700,
              }}
            >
              {err}
            </div>
          ) : null}

          <form onSubmit={handleUpload}>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 950, marginBottom: 6 }}>Mode</div>
                <select
                  value={mode}
                  onChange={(e) => setMode(e.target.value as any)}
                  style={{ padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(0,0,0,0.16)" }}
                >
                  <option value="combined">Combined (Orgs + Students)</option>
                  <option value="orgs">Orgs Only</option>
                  <option value="students">Students Only</option>
                </select>
              </div>

              <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}>
                  <input type="checkbox" checked={returnPins} onChange={(e) => setReturnPins(e.target.checked)} />
                  Return issued PINs (recommended)
                </label>

                <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}>
                  <input type="checkbox" checked={rotatePins} onChange={(e) => setRotatePins(e.target.checked)} />
                  Rotate existing PINs
                </label>
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              style={{
                padding: "10px 14px",
                borderRadius: 12,
                border: "none",
                fontWeight: 950,
                cursor: !canSubmit ? "not-allowed" : "pointer",
                opacity: !canSubmit ? 0.6 : 1,
                background: "#0ea5b7",
                color: "white",
              }}
            >
              {loading ? "Uploading..." : "Upload CSV"}
            </button>
          </form>

          {result ? (
            <div style={{ marginTop: 18 }}>
              <h3 style={{ margin: "10px 0", fontSize: 18, fontWeight: 950 }}>Results</h3>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontWeight: 700 }}>
                <div><b>Mode:</b> {result.mode}</div>
                <div><b>Created:</b> {result.created}</div>
                <div><b>Updated:</b> {result.updated}</div>
                <div><b>Skipped:</b> {result.skipped}</div>
              </div>

              {result.issued_pins?.length ? (
                <div style={{ marginTop: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <h4 style={{ margin: 0, fontSize: 16, fontWeight: 950 }}>
                      Issued PINs ({result.issued_pins.length})
                    </h4>
                    <button
                      type="button"
                      onClick={downloadPinsCsv}
                      style={{
                        padding: "8px 10px",
                        borderRadius: 10,
                        cursor: "pointer",
                        border: "1px solid rgba(0,0,0,0.16)",
                        background: "white",
                        fontWeight: 900,
                      }}
                    >
                      Download CSV
                    </button>
                    {result.pins_truncated ? (
                      <span style={{ color: "#8a2a00", fontWeight: 900 }}>(truncated to 1000)</span>
                    ) : null}
                  </div>

                  <div style={{ marginTop: 10, overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #eee" }}>Org Code</th>
                          <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #eee" }}>Student ID</th>
                          <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #eee" }}>PIN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.issued_pins.map((r, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: 8, borderBottom: "1px solid #f3f3f3" }}>{r.org_code}</td>
                            <td style={{ padding: 8, borderBottom: "1px solid #f3f3f3" }}>{r.student_id}</td>
                            <td style={{ padding: 8, borderBottom: "1px solid #f3f3f3", fontWeight: 950 }}>{r.pin}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: 12, opacity: 0.85, fontWeight: 650 }}>
                  No PINs returned. Turn on <b>Return issued PINs</b> (or use Rotate).
                </div>
              )}

              {result.errors?.length ? (
                <div style={{ marginTop: 14 }}>
                  <h4 style={{ margin: "0 0 8px", fontWeight: 950 }}>Errors (up to 200)</h4>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {result.errors.map((er, idx) => (
                      <li key={idx}>
                        <b>Row {er.row}:</b> {er.error}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div style={{ marginTop: 12, textAlign: "center", fontSize: 12, opacity: 0.85 }}>
          © {new Date().getFullYear()} Doctor Is In App. All rights reserved.
        </div>
      </div>
    </div>
  );
}
